import type { ImpactEffectId, ProjectileEffectId } from './sim/content';

export type CardCastGesture = 'cast' | 'item';

export interface CardPresentationProfile {
  gesture: CardCastGesture;
  color: number;
  projectile?: ProjectileEffectId;
  impact?: ImpactEffectId;
  area?: boolean;
  delayed?: boolean;
}

const ARCANE = 0x80b7ff;
const FIRE = 0xff713c;
const HEAL = 0x62d66f;
const NECRO = 0x9c76d8;
const WARD = 0xe5d28b;
const FROST = 0x8fcfff;

/**
 * Authored presentation for every released spell and item. These are visual
 * identities only; the pure card engine remains the sole rules implementation.
 */
export const CARD_PRESENTATION_PROFILES: Readonly<Record<string, CardPresentationProfile>> = {
  'healing-potion': { gesture: 'item', color: HEAL, impact: 'greater-heal' },
  'greater-heal': { gesture: 'cast', color: HEAL, impact: 'greater-heal' },
  fireball: { gesture: 'cast', color: FIRE, projectile: 'fireball', impact: 'explosion' },
  lightning: { gesture: 'cast', color: ARCANE, impact: 'lightning' },
  'explosion-potion': { gesture: 'item', color: FIRE, impact: 'explosion', area: true },
  'trapped-pouch': { gesture: 'item', color: FIRE, impact: 'explosion' },
  teleport: { gesture: 'cast', color: ARCANE, impact: 'poof' },
  spellbook: { gesture: 'item', color: ARCANE },
  bless: { gesture: 'cast', color: HEAL, impact: 'greater-heal' },
  'energy-bolt': { gesture: 'cast', color: ARCANE, projectile: 'magic-arrow', impact: 'explosion' },
  'flame-strike': { gesture: 'cast', color: FIRE, impact: 'flame-strike' },
  'chain-lightning': { gesture: 'cast', color: ARCANE, impact: 'lightning' },
  earthquake: { gesture: 'cast', color: FIRE, impact: 'big-explosion', area: true },
  resurrection: { gesture: 'cast', color: HEAL, impact: 'greater-heal' },
  'animate-dead': { gesture: 'cast', color: NECRO, impact: 'death-vortex' },
  'blade-spirits': { gesture: 'cast', color: ARCANE, impact: 'poof' },
  'summon-creature': { gesture: 'cast', color: ARCANE, impact: 'poof' },
  explosion: { gesture: 'cast', color: FIRE, impact: 'explosion', delayed: true },
  'vengeful-spirit': { gesture: 'cast', color: NECRO, impact: 'death-vortex' },
  'summon-daemon': { gesture: 'cast', color: NECRO, impact: 'death-vortex' },
  paralyze: { gesture: 'cast', color: FROST, impact: 'lightning' },
  'reactive-armor': { gesture: 'cast', color: WARD, impact: 'greater-heal' },
  'magic-reflection': { gesture: 'cast', color: ARCANE, impact: 'greater-heal' },
  'blood-oath': { gesture: 'cast', color: NECRO, impact: 'death-vortex' },
  'noble-sacrifice': { gesture: 'cast', color: HEAL, impact: 'greater-heal', area: true },
  'word-of-death': { gesture: 'cast', color: NECRO, impact: 'death-vortex' },
  'divine-fury': { gesture: 'cast', color: WARD, impact: 'greater-heal' },
};

export function cardPresentationProfile(cardId: string | undefined): CardPresentationProfile | undefined {
  return cardId ? CARD_PRESENTATION_PROFILES[cardId] : undefined;
}
